import { useQuery } from "@apollo/client/react";
import { ButtonBase, ClickAwayListener, ListItemButton, Popper, Stack, TextField, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretUpIcon } from "@phosphor-icons/react/dist/csr/CaretUp";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccountContext } from "../../strict/app/AccountResolver";
import useAuth from "../../context/UserContext";
import { AccountsForLoggedUserDocument } from "../../graphql/types";
import type { AccountsForLoggedUserQuery, AccountsForLoggedUserQueryVariables } from "../../graphql/types";

const Container = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.surfaceLight10.main,
  border: `1px solid ${theme.palette.surfaceLight30.main}`,
  boxShadow: theme.mixins.shadowSurfaceLight10,
  ...theme.applyStyles("dark", {
    boxShadow: theme.mixins.shadowSurfaceDark10,
  }),
  borderRadius: "4px",
  padding: "24px",
  gap: "16px",
}));

export const AccountMenu = () => {
  const { urlName } = useAccountContext();
  const { user, updateAccounts } = useAuth();
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  const { data: accountsData } = useQuery<AccountsForLoggedUserQuery, AccountsForLoggedUserQueryVariables>(
    AccountsForLoggedUserDocument,
    {
      fetchPolicy: "cache-and-network",
    }
  );

  useEffect(() => {
    if (!accountsData) return;
    const accounts =
      accountsData?.meV2?.accounts.map((account) => {
        return {
          urlName: account.urlName,
          name: account.name,
          show_download_summary: account.show_download_summary,
        };
      }) || [];

    updateAccounts(accounts);

    const currentUrlName = urlName;
    const isValid = accounts.some((acc) => acc.urlName === currentUrlName);

    if (currentUrlName && isValid) {
      localStorage.setItem("urlName", currentUrlName);
    } else if (currentUrlName && !isValid && accounts.length > 0) {
      navigate(`/app/${accounts[0].urlName}/dashboard`);
      localStorage.setItem("urlName", accounts[0].urlName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsData]);

  const handleClick = (e: React.MouseEvent<HTMLElement>) =>
    anchorEl ? setAnchorEl(null) : setAnchorEl(e.currentTarget);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccountSelected = (urlName: string) => {
    setAnchorEl(null);
    navigate(`/app/${urlName}/dashboard`);
  };
  const open = Boolean(anchorEl);

  const accounts = user.accounts;
  if (!urlName) return null;

  const filteredAccounts = accounts?.filter((account) => account.urlName.includes(search));

  return (
    <>
      {accounts && Object.keys(accounts).length > 1 ? (
        <>
          <ButtonBase onClick={handleClick} data-testid="account-name">
            <Stack
              direction="row"
              sx={{ alignItems: "center", justifyContent: "space-between", p: "12px", minWidth: "300px" }}
            >
              <Typography variant="body.large.regular">{urlName}</Typography>
              {open ? <CaretUpIcon size="24px" /> : <CaretDownIcon size="24px" data-testid="caret-down-icon" />}
            </Stack>
          </ButtonBase>

          <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            sx={{
              zIndex: (theme) => theme.zIndex.modal,
            }}
          >
            <ClickAwayListener onClickAway={handleClose}>
              <Container>
                <>
                  <Typography variant="body.small.regular">Select the Account you want to view</Typography>
                  <TextField placeholder="Search Account" onChange={(e) => setSearch(e.target.value)} value={search} />
                  <Stack
                    sx={{
                      maxHeight: "500px",
                      overflowY: "hidden",
                      "&:hover": {
                        overflowY: "auto",
                      },
                    }}
                  >
                    {filteredAccounts.length ? (
                      filteredAccounts.map((account) => (
                        <ListItemButton key={account.urlName} onClick={() => handleAccountSelected(account.urlName)}>
                          <Typography data-testid="selectable-account" variant="body.medium.regular">
                            {account.urlName}
                          </Typography>
                        </ListItemButton>
                      ))
                    ) : (
                      <Typography variant="body.medium.regular">No accounts found</Typography>
                    )}
                  </Stack>
                </>
              </Container>
            </ClickAwayListener>
          </Popper>
        </>
      ) : (
        <Stack sx={{ p: "12px" }}>
          <Typography variant="body.large.regular" data-testid="account-name">
            {urlName}
          </Typography>
        </Stack>
      )}
    </>
  );
};
